import React, { useEffect } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  Medication as MedicationIcon,
  Task as TaskIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Notifications as SystemIcon,
  Message as CommunicationIcon,
} from '@mui/icons-material';
import { Notification } from '../../services/notifications';

interface NotificationToastProps {
  notification: Notification | null;
  open: boolean;
  onClose: () => void;
  onAction?: () => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  open,
  onClose,
  onAction,
}) => {
  useEffect(() => {
    if (open && notification) {
      // Auto-dismiss after 5 seconds for non-critical, 10 seconds for critical
      const timeout = setTimeout(
        () => {
          onClose();
        },
        notification.severity === 'critical' ? 10000 : 5000
      );

      return () => clearTimeout(timeout);
    }
  }, [open, notification, onClose]);

  if (!notification) return null;

  const getIcon = () => {
    switch (notification.type) {
      case 'medication':
        return <MedicationIcon />;
      case 'task':
        return <TaskIcon />;
      case 'alert':
        return <WarningIcon />;
      case 'system':
        return <SystemIcon />;
      case 'communication':
        return <CommunicationIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverity = (): 'error' | 'warning' | 'info' | 'success' => {
    switch (notification.severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'info';
      default:
        return 'info';
    }
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    onClose();
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={notification.severity === 'critical' ? 10000 : 5000}
      onClose={onClose}
    >
      <Alert
        severity={getSeverity()}
        icon={getIcon()}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {notification.action_url && (
              <Button
                size="small"
                onClick={handleAction}
                color="inherit"
                sx={{ minWidth: 'auto', ml: 1, px: 1 }}
              >
                {notification.action_label || 'View'}
              </Button>
            )}
            <IconButton
              size="small"
              onClick={onClose}
              color="inherit"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          minWidth: 300,
          maxWidth: 500,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <AlertTitle>{notification.title}</AlertTitle>
        {notification.message}
      </Alert>
    </Snackbar>
  );
};

export default NotificationToast;

